import { Link, type ErrorComponentProps } from "@tanstack/react-router";
import { ScreenCard } from "./screen-card";
import { MissionButton } from "./mission-button";
import { FALLBACK } from "../lib/copy";

/**
 * Router-level fallbacks, styled in the funnel's own chrome so a bad link
 * or a render crash never drops a visitor onto a bare default page.
 * Registered in src/main.tsx (defaultNotFoundComponent /
 * defaultErrorComponent). Keep these dependency-light: no Redux, no
 * TanStack Query — they must render even when app state is the problem.
 */

export function NotFoundScreen() {
  return (
    <ScreenCard stage={FALLBACK.notFound.stage}>
      <h1 className="mb-4 text-center font-serif text-[22px] font-bold text-foreground">
        {FALLBACK.notFound.title}
      </h1>
      <p className="text-center font-serif text-lg leading-relaxed text-parchment">
        {FALLBACK.notFound.line}
      </p>
      <div className="mt-6 flex justify-center">
        <MissionButton asChild className="flex-none">
          <Link to="/">{FALLBACK.notFound.action}</Link>
        </MissionButton>
      </div>
    </ScreenCard>
  );
}

export function AppErrorScreen({ error, reset }: ErrorComponentProps) {
  return (
    <ScreenCard stage={FALLBACK.error.stage}>
      <h1 className="mb-4 text-center font-serif text-[22px] font-bold text-[#d8635c] [text-shadow:0_0_18px_rgba(216,99,92,0.3)]">
        {FALLBACK.error.title}
      </h1>
      <p className="text-center font-serif text-lg leading-relaxed text-parchment">
        {FALLBACK.error.line}
      </p>
      {import.meta.env.DEV && (
        <pre className="mt-4 overflow-x-auto rounded-lg border border-border bg-black/40 p-3 text-left font-mono text-[11px] leading-relaxed text-muted-foreground">
          {error instanceof Error
            ? (error.stack ?? error.message)
            : String(error)}
        </pre>
      )}
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <MissionButton onClick={reset} className="flex-none">
          {FALLBACK.error.retry}
        </MissionButton>
      </div>
      <Link
        to="/"
        className="mx-auto mt-6 block text-center font-serif text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
      >
        {FALLBACK.error.home}
      </Link>
    </ScreenCard>
  );
}
